import { useEffect, useState } from "react";
import { ActionIcon, Alert, Anchor, Button, Card, CopyButton, Grid, Group, List, NumberInput, Stack, Table, Text, TextInput, Title } from "@mantine/core";
import { IconExternalLink, IconPlus, IconShoppingCart, IconSparkles, IconTrash } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { humanizedToShoppingItems, shoppingListText, type ShoppingItem } from "@passio/api-client";
import { passio } from "../lib/clients";
import { PageHeader } from "../theme/components";
import { EmptyState, ErrorNotice, RequireFeature, ResultHeader } from "../components/common";
import { CodeBlock, JsonView } from "../components/JsonView";
import { RouteModelPicker } from "../components/ModelPicker";

/** Recipe units on purpose, so "Shopper-friendly" has something to convert. */
const START: ShoppingItem[] = [
  { name: "salmon fillet", portionQuantity: 450, portionSize: "g" },
  { name: "cooked quinoa", portionQuantity: 3, portionSize: "cups" },
  { name: "baby spinach", portionQuantity: 200, portionSize: "g" },
  { name: "lemon juice", portionQuantity: 4, portionSize: "tbsp" },
  { name: "extra virgin olive oil", portionQuantity: 3, portionSize: "tbsp" },
];

/** Other pages (e.g. Meal plans) can hand over a list via navigation state. */
export interface ShoppingHandoff {
  items: ShoppingItem[];
  title?: string;
}

export function ShoppingPage() {
  const location = useLocation();
  const handoff = location.pathname === "/shopping" ? (location.state as ShoppingHandoff | null) : null;
  const [items, setItems] = useState<ShoppingItem[]>(() => (handoff?.items?.length ? handoff.items : START));
  const [title, setTitle] = useState(handoff?.title ?? "Salmon & quinoa week");
  // The page stays mounted between visits, so a new hand-off has to replace the list it already has.
  const [seen, setSeen] = useState(handoff);
  if (handoff?.items?.length && handoff !== seen) {
    setSeen(handoff);
    setItems(handoff.items);
    setTitle(handoff.title ?? title);
  }
  const [campaign, setCampaign] = useState("passio-demo");
  const m = useMutation({
    mutationFn: () => passio.affiliates.convertShoppingList(items.filter((i) => i.name.trim()), { title: title || undefined, campaign: campaign || undefined, sourceTool: "passio-demo" }),
  });
  const humanize = useMutation({
    mutationFn: () => passio.tools.humanizeShoppingList(shoppingListText(items)),
    onSuccess: (r) => {
      const next = humanizedToShoppingItems(r.data);
      if (next.length) setItems(next);
    },
  });
  const notes = (humanize.data?.data.items ?? []).filter((i) => i.notes || i.cookingStateAdjusted);
  const { reset } = m;
  const { reset: resetHumanize } = humanize;
  useEffect(() => {
    reset();
    resetHumanize();
  }, [seen, reset, resetHumanize]);
  const set = (i: number, patch: Partial<ShoppingItem>) => setItems((x) => x.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  const res = m.data?.data;

  return (
    <RequireFeature feature="shopping">
      <PageHeader
        eyebrow="POST /napi/tools/humanizeShoppingList · /napi/affiliates/convertShoppingListToAffiliateLink"
        title="Shopping cart"
        description="Rewrite recipe amounts (450 g, 3 tbsp) into what a shopper buys, then turn the list (or a meal-plan result) into a tracked Instacart link with drop-in buttons that already point at it."
        actions={<RouteModelPicker routes={["humanizeShoppingList", "convertShoppingListToAffiliateLink"]} />}
      />
      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, lg: 6 }}>
          <Card>
            <Table verticalSpacing={6}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Item</Table.Th>
                  <Table.Th w={90}>Qty</Table.Th>
                  <Table.Th w={110}>Unit</Table.Th>
                  <Table.Th w={36} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map((it, i) => (
                  <Table.Tr key={i}>
                    <Table.Td>
                      <TextInput size="xs" value={it.name} onChange={(e) => set(i, { name: e.currentTarget.value })} />
                    </Table.Td>
                    <Table.Td>
                      <NumberInput size="xs" hideControls value={it.portionQuantity ?? ""} onChange={(v) => set(i, { portionQuantity: v === "" ? undefined : Number(v) })} />
                    </Table.Td>
                    <Table.Td>
                      <TextInput size="xs" value={it.portionSize ?? ""} onChange={(e) => set(i, { portionSize: e.currentTarget.value || undefined })} />
                    </Table.Td>
                    <Table.Td>
                      <ActionIcon variant="subtle" color="gray" onClick={() => setItems((x) => x.filter((_, j) => j !== i))} aria-label="Remove item">
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            <Group justify="space-between" mt="xs">
              <Button variant="subtle" color="ink" size="xs" leftSection={<IconPlus size={14} />} onClick={() => setItems((x) => [...x, { name: "" }])}>
                Add item
              </Button>
              <Button variant="light" color="violet" size="xs" leftSection={<IconSparkles size={14} />} loading={humanize.isPending} disabled={!items.some((i) => i.name.trim())} onClick={() => humanize.mutate()}>
                Shopper-friendly quantities
              </Button>
            </Group>
            {humanize.error && <ErrorNotice error={humanize.error} onRetry={() => humanize.mutate()} />}
            {humanize.data && !humanize.isPending && (
              <Alert color="violet" variant="light" mt="xs" title="Rewritten by humanizeShoppingList">
                <Text fz="xs">Quantities above are now in units a shopper buys. Edit anything before creating the cart.</Text>
                {notes.length > 0 && (
                  <List size="xs" mt={4}>
                    {notes.map((n, i) => (
                      <List.Item key={i}>
                        <b>{n.shoppingName || n.name}</b>
                        {n.cookingStateAdjusted ? " (adjusted from cooked to raw)" : ""}
                        {n.notes ? `: ${n.notes}` : ""}
                      </List.Item>
                    ))}
                  </List>
                )}
              </Alert>
            )}
            <Group grow mt="md">
              <TextInput label="Landing page title" value={title} onChange={(e) => setTitle(e.currentTarget.value)} />
              <TextInput label="Campaign" value={campaign} onChange={(e) => setCampaign(e.currentTarget.value)} />
            </Group>
            <Button mt="md" fullWidth size="md" leftSection={<IconShoppingCart size={18} />} loading={m.isPending} onClick={() => m.mutate()} disabled={!items.some((i) => i.name.trim())}>
              Create cart link
            </Button>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, lg: 6 }}>
          {m.error && <ErrorNotice error={m.error} onRetry={() => m.mutate()} />}
          {res ? (
            <Card>
              <ResultHeader title={`Cart via ${res.affiliate}`} meta={m.data?.meta} />
              <Stack gap="md">
                <Group gap="xs" wrap="nowrap">
                  <Button component="a" href={res.passio_redirect_url} target="_blank" rel="noreferrer" rightSection={<IconExternalLink size={16} />}>
                    Open cart
                  </Button>
                  <CopyButton value={res.passio_redirect_url}>
                    {({ copied, copy }) => (
                      <Button variant="default" onClick={copy}>
                        {copied ? "Copied" : "Copy link"}
                      </Button>
                    )}
                  </CopyButton>
                </Group>
                <Text fz="xs" c="dimmed" style={{ wordBreak: "break-all" }}>
                  {res.passio_redirect_url}
                </Text>
                {res.button_options?.length ? (
                  <div>
                    <Title order={4} fz="sm" mb="xs">
                      Drop-in buttons
                    </Title>
                    <Stack gap="md">
                      {res.button_options.map((b) => (
                        <Card key={b.id} bg="var(--pd-cream)" p="sm">
                          <Group justify="space-between" mb={6}>
                            <Text fw={650} fz="sm">
                              {b.label}
                            </Text>
                            <Text fz="xs" c="dimmed" ff="monospace">
                              {b.id}
                            </Text>
                          </Group>
                          <Anchor href={res.passio_redirect_url} target="_blank" rel="noreferrer">
                            <img src={b.image_url} alt={b.alt_text} style={{ height: 46 }} />
                          </Anchor>
                          {b.description && (
                            <Text fz="xs" c="dimmed" mt={4}>
                              {b.description}
                            </Text>
                          )}
                          <CodeBlock code={b.html} maxHeight={120} language="html" />
                        </Card>
                      ))}
                    </Stack>
                  </div>
                ) : null}
                {res.tracking_context && (
                  <div>
                    <Title order={4} fz="sm" mb="xs">
                      Tracking context
                    </Title>
                    <JsonView value={res.tracking_context} maxHeight={220} />
                  </div>
                )}
              </Stack>
            </Card>
          ) : (
            !m.error && <EmptyState title="Your cart link appears here">Links log a first-party click before redirecting to the partner, so attribution works without cookies.</EmptyState>
          )}
        </Grid.Col>
      </Grid>
    </RequireFeature>
  );
}
